import { defineStore } from "pinia";
import { isNetworkUnavailable } from "@/composables/network/offlineMode";
import db from "@/db";
import { useinstallationPhotos } from "@/composables/installations/photos/useInstallationPhotos";
import { config, fetchAll } from "@/setup/openApiConfig";
import {
  ProjectDocumentsApi,
  ProjectDocumentsListOrderingEnum,
  DocumentTypeApi,
  ProjectFilesApi,
  TagsApi,
} from "@socotec.io/document-sdk";
import { uploadFile } from "@/setup/bimdataClient";
import { useProjectsStore } from "@/store/modules/projects";
import { OfflineError } from "@/utils/errors";
import { isOfflineFailure, logRequestFailure } from "@/utils/network";

const documentAPi = new ProjectDocumentsApi(config);
const documentTypeApi = new DocumentTypeApi(config);
const projectFilesApi = new ProjectFilesApi(config);
const tagsApi = new TagsApi(config);

const QAL2_AST_EXPORT_KIND = "QAL2_AST_EXPORT";

export const useDocumentsStore = defineStore("documents", {
  state: () => ({
    documentsCount: 0,

    tags: [],
    tagsResponse: null,
    tagsProjectUuid: null,
    documentTypes: [],
    documentIsLoading: true,
    currentImages: [],
  }),
  getters: {
    getDocument: () => async (documentUuid) =>
      db.documents.get({ uuid: documentUuid }),
    documentTypePhoto: (state) =>
      state.documentTypes.find(({ name }) => name === "PHOTO"),
    getDocumentsByUuids: () => async (documentUuids) =>
      db.documents.where("uuid").anyOf(documentUuids).toArray(),
    tagsNames: (state) => [...new Set(state.tags.map((tag) => tag.name))],
  },
  actions: {
    async fetchDocumentTypes() {
      try {
        this.documentTypes = await documentTypeApi.list();
      } catch (error) {
        logRequestFailure("Error fetching document types:", error);
      }
    },

    async fetchDocumentByUuid(uuid) {
      const document = await documentAPi.retrieve({
        uuid,
        relatedObjectPk: useProjectsStore().currentProjectUuid,
      });
      return document;
    },

    async fetchInstallationImagesByUuids(uuids) {
      this.documentIsLoading = true;
      try {
        const projectUuid = useProjectsStore().currentProjectUuid;
        const response = await documentAPi.list({
          relatedObjectPk: projectUuid,
          uuids,
        });
        for (const document of response.results) {
          document.relatedObjects = [projectUuid];
          await db.models.Document.createOrUpdate(document);
        }
        this.currentImages = response.results;

        return response.results;
      } finally {
        this.documentIsLoading = false;
      }
    },

    async fetchDocuments({ projectUuid, query }) {
      if (isNetworkUnavailable()) throw new OfflineError();
      this.documentIsLoading = true;
      try {
        try {
          const { page_size: pageSize, ...filters } = query ?? {};
          const response = await documentAPi.list({
            relatedObjectPk: projectUuid,
            ...filters,
            ...(pageSize && { pageSize: Number(pageSize) }),
          });
          this.documentsCount = response.count;
          this.documentIsLoading = false;

          return response.results;
        } catch (error) {
          this.documentIsLoading = false;
          logRequestFailure("Error fetching documents:", error);
          if (isOfflineFailure(error)) throw error;
          throw new Error("Failed to fetch documents");
        }
      } finally {
        this.documentIsLoading = false;
      }
    },

    async fetchQal2AstExports(projectUuid) {
      if (useProjectsStore().isOfflineState) throw new OfflineError();
      const { results } = await fetchAll(documentAPi, "list", {
        relatedObjectPk: projectUuid,
        metaDatasValuesIn: JSON.stringify({ kind: [QAL2_AST_EXPORT_KIND] }),
        ordering: [ProjectDocumentsListOrderingEnum.CreatedAt],
      });
      return results;
    },

    async getDocumentUrl(uuid) {
      const file = await projectFilesApi.retrieve({
        relatedObjectPk: useProjectsStore().currentProjectUuid,
        uuid,
      });
      return file.url;
    },

    async createDocument({ form, file, uploadResponse }) {
      this.tagsResponse = null;
      const project = useProjectsStore().currentProject;
      if (!project.originId) {
        throw new Error("Project origin ID is missing");
      }
      const upload =
        uploadResponse ??
        (file ? await uploadFile(project.originId, file) : null);

      if (upload) {
        form.originId = upload.id;
        form.mimeType = upload.mimeType;
        form.gedProjectId = project.originId;
      }

      form.servicesRelatedName = [import.meta.env.VITE_APP_ATMO_SERVICE_ID];

      const response = await documentAPi.create({
        relatedObjectPk: project.uuid,
        documentCreate: form,
      });

      await db.documents.add(response);
      return response;
    },

    async updateDocument({ form }) {
      this.tagsResponse = null;
      try {
        const response = await documentAPi.update({
          documentUpdate: form,
          relatedObjectPk: useProjectsStore().currentProjectUuid,
          uuid: form.uuid,
        });
        await db.documents.update(response.uuid, response);
      } catch (error) {
        logRequestFailure(error);
        if (isOfflineFailure(error)) throw error;
        throw new Error("Failed to update document");
      }
    },

    async updateMultipleDocuments({ data, documentUuids }) {
      this.tagsResponse = null;
      const response = await documentAPi.bulkUpdate({
        relatedObjectPk: useProjectsStore().currentProjectUuid,
        documentUpdate: documentUuids.map((uuid) => ({ uuid, ...data })),
      });
      await Promise.all(
        response.map((document) => db.documents.update(document.uuid, document))
      );
    },

    async removeInstallationPicture({ installation, uuid }) {
      const { removePhotoFromInstallation } = useinstallationPhotos();
      await documentAPi.destroy({
        relatedObjectPk: useProjectsStore().currentProjectUuid,
        uuid,
      });
      await removePhotoFromInstallation(installation, uuid);
      await db.documents.delete(uuid);
      this.currentImages = this.currentImages.filter(
        (image) => image.uuid !== uuid
      );
    },

    async removeDocument(uuid) {
      await documentAPi.destroy({
        relatedObjectPk: useProjectsStore().currentProjectUuid,
        uuid,
      });
      await db.documents.delete(uuid);
    },

    addImage(image) {
      this.currentImages.push(image);
    },

    setCurrentImages(images) {
      this.currentImages = images;
    },

    async fetchDocumentTags(projectUuid) {
      if (this.tagsResponse && this.tagsProjectUuid === projectUuid) {
        return this.tagsResponse;
      }
      try {
        const response = await fetchAll(tagsApi, "list", {
          relatedObjectPk: projectUuid,
        });
        this.tags = response.results;
        this.tagsResponse = response;
        this.tagsProjectUuid = projectUuid;
        return response;
      } catch (error) {
        console.error(error);
      }
    },
  },
});
